import React, {createContext, useContext, useEffect, useState, ReactNode} from 'react'

// Theme types
export type Theme = 'light' | 'dark'

// Theme context interface
interface ThemeContextType {
  theme: Theme
  toggleTheme: () => void
  setTheme: (theme: Theme) => void
}

// Create context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

// Local storage key
const THEME_STORAGE_KEY = 'omicsbox-theme-preference'

// Theme provider props
interface ThemeProviderProps {
  children: ReactNode
}

// Get initial theme from localStorage or default to light
const getInitialTheme = (): Theme => {
  try {
    // First check localStorage
    const storedTheme = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null
    if (storedTheme && (storedTheme === 'light' || storedTheme === 'dark')) {
      return storedTheme
    }
  } catch (error) {
    console.warn('Failed to get theme preference:', error)
  }

  // Always default to light theme
  return 'light'
}

// Apply theme to document
// `data-theme` drives our own `[data-theme='dark']` overrides; `data-bs-theme` switches
// Bootstrap 5.3's built-in color-mode CSS variables for its components.
const applyTheme = (theme: Theme) => {
  document.documentElement.setAttribute('data-theme', theme)
  document.documentElement.setAttribute('data-bs-theme', theme)
}

// Theme provider component
export const ThemeProvider: React.FC<ThemeProviderProps> = ({children}) => {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme)

  // Apply theme on mount and theme change
  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  // Save theme to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme)
    } catch (error) {
      console.warn('Failed to save theme preference:', error)
    }
  }, [theme])

  // Note: Removed system theme change listener to ensure dark mode is off by default
  // Users must explicitly choose dark mode through the UI

  // Toggle between light and dark
  const toggleTheme = () => {
    setThemeState((prevTheme) => (prevTheme === 'light' ? 'dark' : 'light'))
  }

  // Set specific theme
  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme)
  }

  const value: ThemeContextType = {
    theme,
    toggleTheme,
    setTheme,
  }

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

// Hook to use theme context
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

// Theme toggle component
interface ThemeToggleProps {
  className?: string
  size?: 'sm' | 'md' | 'lg'
  showLabel?: boolean
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  className = '',
  size = 'md',
  showLabel = true,
}) => {
  const {theme, toggleTheme} = useTheme()

  const sizeClasses = {
    sm: 'btn-sm',
    md: '',
    lg: 'btn-lg',
  }

  return (
    <div className={`d-flex align-items-center ${className}`}>
      {showLabel && (
        <span className='me-3'>
          <i className={`fas ${theme === 'dark' ? 'fa-moon' : 'fa-sun'} me-2`}></i>
          {theme === 'dark' ? 'Dark Mode' : 'Light Mode'}
        </span>
      )}
      <button
        onClick={toggleTheme}
        className={`btn btn-outline-secondary ${sizeClasses[size]}`}
        title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
        type='button'
      >
        <i className={`fas ${theme === 'dark' ? 'fa-sun' : 'fa-moon'}`}></i>
        {showLabel && <span className='ms-2'>{theme === 'dark' ? 'Light' : 'Dark'}</span>}
      </button>
    </div>
  )
}

export default ThemeProvider
